'use client';

import { ArrowRightLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

type CategoryId = 'length' | 'weight' | 'temperature' | 'volume' | 'speed';

const CATEGORIES: { id: CategoryId; label: string }[] = [
  { id: 'length', label: 'Length' },
  { id: 'weight', label: 'Weight' },
  { id: 'temperature', label: 'Temperature' },
  { id: 'volume', label: 'Volume' },
  { id: 'speed', label: 'Speed' },
];

// Linear categories: factor converts 1 unit -> base unit.
const LINEAR_UNITS: Record<
  Exclude<CategoryId, 'temperature'>,
  { id: string; label: string; factor: number }[]
> = {
  length: [
    { id: 'mm', label: 'Millimeters', factor: 0.001 },
    { id: 'cm', label: 'Centimeters', factor: 0.01 },
    { id: 'm', label: 'Meters', factor: 1 },
    { id: 'km', label: 'Kilometers', factor: 1000 },
    { id: 'in', label: 'Inches', factor: 0.0254 },
    { id: 'ft', label: 'Feet', factor: 0.3048 },
    { id: 'yd', label: 'Yards', factor: 0.9144 },
    { id: 'mi', label: 'Miles', factor: 1609.344 },
  ],
  weight: [
    { id: 'mg', label: 'Milligrams', factor: 0.001 },
    { id: 'g', label: 'Grams', factor: 1 },
    { id: 'kg', label: 'Kilograms', factor: 1000 },
    { id: 'oz', label: 'Ounces', factor: 28.3495 },
    { id: 'lb', label: 'Pounds', factor: 453.592 },
  ],
  volume: [
    { id: 'ml', label: 'Milliliters', factor: 0.001 },
    { id: 'l', label: 'Liters', factor: 1 },
    { id: 'cup', label: 'Cups (US)', factor: 0.24 },
    { id: 'gal', label: 'Gallons (US)', factor: 3.78541 },
  ],
  speed: [
    { id: 'mps', label: 'Meters/second', factor: 1 },
    { id: 'kmh', label: 'Kilometers/hour', factor: 0.277778 },
    { id: 'mph', label: 'Miles/hour', factor: 0.44704 },
    { id: 'knot', label: 'Knots', factor: 0.514444 },
  ],
};

const TEMPERATURE_UNITS = [
  { id: 'c', label: 'Celsius' },
  { id: 'f', label: 'Fahrenheit' },
  { id: 'k', label: 'Kelvin' },
];

function toCelsius(value: number, unit: string) {
  if (unit === 'c') return value;
  if (unit === 'f') return ((value - 32) * 5) / 9;
  return value - 273.15;
}

function fromCelsius(celsius: number, unit: string) {
  if (unit === 'c') return celsius;
  if (unit === 'f') return (celsius * 9) / 5 + 32;
  return celsius + 273.15;
}

export function UnitConverter() {
  const [category, setCategory] = useState<CategoryId>('length');
  const units = category === 'temperature' ? TEMPERATURE_UNITS : LINEAR_UNITS[category];
  const [from, setFrom] = useState(units[0].id);
  const [to, setTo] = useState(units[1]?.id ?? units[0].id);
  const [value, setValue] = useState('1');

  useEffect(() => {
    const next = category === 'temperature' ? TEMPERATURE_UNITS : LINEAR_UNITS[category];
    setFrom(next[0].id);
    setTo(next[1]?.id ?? next[0].id);
  }, [category]);

  const result = useMemo(() => {
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n)) return null;

    if (category === 'temperature') return fromCelsius(toCelsius(n, from), to);

    const list = LINEAR_UNITS[category];
    const fromFactor = list.find((u) => u.id === from)?.factor ?? 1;
    const toFactor = list.find((u) => u.id === to)?.factor ?? 1;
    return (n * fromFactor) / toFactor;
  }, [category, from, to, value]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setCategory(c.id)}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
              category === c.id
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-[1fr_auto_1fr] items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">From</span>
          <select
            name="unit-from"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-2 text-sm text-foreground"
          >
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => {
            setFrom(to);
            setTo(from);
          }}
          aria-label="Swap units"
          className="mb-0.5 inline-flex size-10 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted"
        >
          <ArrowRightLeft className="size-4" />
        </button>
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">To</span>
          <select
            name="unit-to"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-2 text-sm text-foreground"
          >
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="mt-4 block">
        <span className="text-xs font-medium text-muted-foreground">Value</span>
        <input
          type="number"
          name="unit-value"
          autoComplete="off"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mt-1 h-11 w-full rounded-lg border border-input bg-background px-3 text-lg text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
        />
      </label>

      <div className="mt-6 rounded-xl bg-primary/10 px-4 py-4">
        <p className="text-xs font-medium text-muted-foreground">Result</p>
        <p className="text-2xl font-bold text-primary">
          {result !== null ? result.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}
        </p>
      </div>
    </div>
  );
}
